import { useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Filter,
  Search,
  Building2,
  Mail,
  Phone,
  Calendar,
  FileText,
  Sparkles,
  Activity,
  X,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
// SheetHeader/SheetTitle used both in CRM.jsx wrapper (sr-only) and inside CompanyProfile
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CRM_LEADS, PIPELINE_STAGES } from "@/data/mockData";
import CRMPipeline from "./lead-finder/pages/CRMPipeline";

export default function CRM() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const filtered = CRM_LEADS.filter(
    (l) =>
      q === "" ||
      l.company.toLowerCase().includes(q.toLowerCase()) ||
      l.contact.toLowerCase().includes(q.toLowerCase())
  );

  const totalValue = filtered.reduce((sum, l) => (["won", "lost"].includes(l.stage) ? sum : sum + l.value), 0);

  return (
    <div data-testid="crm-page">
      <PageHeader
        eyebrow="Unified CRM"
        title="Every lead. Every module. One pipeline."
        description="Freelance jobs, RFPs, local businesses, LinkedIn prospects and Instagram DMs all flow into one Kanban — with AI notes and lifecycle scoring."
      // actions={
      //   <Button data-testid="crm-new-lead" className="rounded-full bg-indigo-500 hover:bg-indigo-400">
      //     <Plus className="h-3.5 w-3.5 mr-1.5" /> New lead
      //   </Button>
      // }
      />

      <div className="h-[80vh]">
        <CRMPipeline />
      </div>

    </div>
  );
}

function CompanyProfile({ lead, onClose }) {
  return (
    <div className="h-full flex flex-col">
      <div className="p-6 border-b border-white/[0.06] relative">
        <button
          onClick={onClose}
          data-testid="crm-close-drawer"
          className="absolute right-4 top-4 text-white/50 hover:text-white p-1"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-display text-2xl text-white leading-tight">
              {lead.company}
            </div>
            <div className="text-[13px] text-white/50 mt-0.5">{lead.contact}</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <MiniKV label="Value" value={`$${(lead.value / 1000).toFixed(0)}k`} />
          <MiniKV label="Score" value={lead.score} />
          <MiniKV label="Source" value={lead.source} />
        </div>
      </div>

      <Tabs defaultValue="timeline" className="flex-1 flex flex-col">
        <div className="px-6 pt-4">
          <TabsList className="bg-white/[0.03] border border-white/[0.06] rounded-full p-1 h-auto">
            {["timeline", "emails", "docs", "ai"].map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                data-testid={`crm-tab-${t}`}
                className="rounded-full data-[state=active]:bg-white/[0.08] data-[state=active]:text-white text-white/60 px-3 py-1 text-xs capitalize"
              >
                {t === "ai" ? "AI Notes" : t}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <TabsContent value="timeline" className="mt-0 space-y-3">
            {[
              { t: "3m ago", who: "AI Copilot", what: "Generated tailored discovery email", tone: "ai" },
              { t: "2h ago", who: "n8n · Outreach", what: "Delivered follow-up #2", tone: "flow" },
              { t: "1d ago", who: "You", what: "Logged discovery call · 32 min", tone: "user" },
              { t: "3d ago", who: "Website Bot", what: "Detected Shopify Plus + Klaviyo", tone: "ai" },
              { t: "5d ago", who: "Lead Engine", what: "Imported from LinkedIn", tone: "flow" },
            ].map((e, i) => (
              <div key={i} className="flex gap-3">
                <div className="mt-1.5">
                  <span className={`inline-block h-2 w-2 rounded-full ${e.tone === "ai" ? "bg-indigo-400 shadow-[0_0_6px_#5E6AD2]" :
                    e.tone === "flow" ? "bg-cyan-400 shadow-[0_0_6px_#00e5ff]" :
                      "bg-white/50"
                    }`} />
                </div>
                <div className="flex-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-[13px] text-white">{e.who}</div>
                    <div className="font-mono text-[10px] text-white/40">{e.t}</div>
                  </div>
                  <div className="text-[12.5px] text-white/60 mt-0.5">{e.what}</div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="emails" className="mt-0 space-y-2">
            {[
              { subj: "Re: Shopify Plus migration — timeline", from: "Devon Reilly", when: "2h" },
              { subj: "Kickoff prep for next Tuesday", from: "You", when: "1d" },
              { subj: "Intro — Base2Brand + Nova Athletics", from: "You", when: "5d" },
            ].map((m, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 flex items-center gap-3">
                <Mail className="h-4 w-4 text-white/40" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white truncate">{m.subj}</div>
                  <div className="text-[11.5px] text-white/50">{m.from}</div>
                </div>
                <div className="font-mono text-[10px] text-white/40">{m.when}</div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="docs" className="mt-0 space-y-2">
            {["Discovery-notes.pdf", "Shopify-Plus-Proposal-v3.pdf", "Migration-Plan.docx"].map((d) => (
              <div key={d} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 flex items-center gap-3">
                <FileText className="h-4 w-4 text-white/40" />
                <div className="flex-1 text-[13px] text-white">{d}</div>
                <span className="font-mono text-[10px] text-white/40">v3</span>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="ai" className="mt-0 space-y-3">
            <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-3.5 w-3.5 text-indigo-300" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-indigo-300">AI note · 2h ago</span>
              </div>
              <p className="text-[13.5px] text-white/85 leading-relaxed">
                Strong signal: {lead.contact} referenced GA4 server-side tagging on their last call — align proposal to include our tagging playbook. Estimated close: 12 days.
              </p>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-3.5 w-3.5 text-cyan-300" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-cyan-300">Automation history</span>
              </div>
              <p className="text-[13px] text-white/70 leading-relaxed">
                12 automations triggered · 3 emails sent · 1 meeting booked · Last audit 3 days ago
              </p>
            </div>
          </TabsContent>
        </div>
      </Tabs>

      <div className="p-4 border-t border-white/[0.06] flex items-center gap-2">
        <Button variant="ghost" size="sm" className="rounded-full text-white/70 border border-white/[0.06]">
          <Phone className="h-3.5 w-3.5 mr-1.5" /> Log call
        </Button>
        <Button variant="ghost" size="sm" className="rounded-full text-white/70 border border-white/[0.06]">
          <Calendar className="h-3.5 w-3.5 mr-1.5" /> Book meeting
        </Button>
        <div className="flex-1" />
        <Button size="sm" className="rounded-full bg-indigo-500 hover:bg-indigo-400">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> AI action
        </Button>
      </div>
    </div>
  );
}

function MiniKV({ label, value }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2.5">
      <div className="font-mono text-[10px] uppercase tracking-wider text-white/50">{label}</div>
      <div className="mt-0.5 font-mono text-[14px]">{value}</div>
    </div>
  );
}
